import _ from 'lodash';
import { GraphHierarchyLink, GraphHierarchyNode } from './model';
import {
  InventoryGraphEdge,
  InventoryGraphNode,
  InventoryResourceGenericRelation,
  ResourceStatus,
  ResourceCategory,
} from 'src/app/models/api/inventoryResource.model';
import { AiService } from 'src/app/models/api/agentStep.model';
import { getResourceColoredIconPath } from 'src/app/common/categoryUtils';
import { getAgentIconUrl } from 'src/app/common/aiServiceUtils';
import { Selection } from 'd3';
import { ResourceType } from 'src/app/models/api/common';

type EnrichedInventoryGraphNode = InventoryGraphNode & { aiService?: AiService | null };

export const GROUP_RESOURCE_TYPES: ResourceType[] = [ResourceType.azure_active_directory_group];

export const minZoom = 0.5;
export const maxZoom = 3;
export const maxInitialZoom = 1.2;
export const radius = 16;
export const fullScreenMargin = 64;
const textOffset = radius + 16;
const textPathElevation = 3;
export const singleNodeHeight = radius * 2 + textOffset + 10;
export const diameter = radius * 2;

// Recursively count the number of node children
export const countChildren = (node: GraphHierarchyNode): number =>
  _.sum(node?.children?.map((child) => countChildren(child)) ?? [1]);

export const pathCurve = (d: GraphHierarchyLink) =>
  `M${d.source.y + radius},${d.source.x}` +
  `C${(d.source.y + d.target.y) / 2},${d.source.x}` +
  ` ${(d.source.y + d.target.y) / 2},${d.target.x}` +
  ` ${d.target.y - radius},${d.target.x}`;

export const pathTextTransform = (d: GraphHierarchyLink) => {
  const translateX = (d.source.y + d.target.y) / 2;
  const translateY = (d.source.x + d.target.x) / 2 - textPathElevation;
  const deltaV = (d.target.x - d.source.x) * 2;
  const deltaH = d.target.y - d.source.y;
  const rotation = (Math.atan(deltaV / deltaH) / Math.PI) * 180;
  return `translate(${translateX} ${translateY}) rotate(${rotation})`;
};

// Map a string array such as ['usedBy', 'createdBy'] to a string in the form "used by, created by"
export const edgeRelationType = (edge: InventoryGraphEdge) => edge.relationTypes.map(relationDisplayName).join(', ');

// Map a 'usedBy' relation name to the string 'used by'
export const relationDisplayName = (relationName: string) => {
  const parenMatch = relationName.match(/^(.*?)(\s*\(.*\))$/);
  const base = parenMatch ? parenMatch[1] : relationName;
  const suffix = parenMatch ? parenMatch[2] : '';
  const formatted = base
    .split(/(?=[A-Z])/)
    .map((token) => token.toLowerCase())
    .join(' ');
  return formatted + suffix;
};

export const nodeTransform = (node: GraphHierarchyNode) => `translate(${node.y}, ${node.x})`;

export const imageTransform = () => `translate(${-radius}, ${-radius})`;

export const textTransform = () => `translate(0, ${textOffset})`;

export const targetUrl = (relation: InventoryResourceGenericRelation) => relation._links.self.href;

export const isActive = (node: InventoryGraphNode) => node.resource.status === ResourceStatus.active;

export const isNodeInactive = (node: GraphHierarchyNode) => node.data.resource.status !== ResourceStatus.active;

export const isLinkInactive = (node: GraphHierarchyLink) => isNodeInactive(node.target);

export const nodeIconUrl = (node: GraphHierarchyNode) => {
  if (node.data.iconUrl) {
    return node.data.iconUrl;
  }

  // For bot nodes, check if they have an AI service mapping
  if (node.data.resource.category === ResourceCategory.bot) {
    const enrichedData = node.data as EnrichedInventoryGraphNode;

    if (enrichedData.aiService) {
      const aiServiceIcon = getAgentIconUrl(node.data.resource.type, enrichedData.aiService);
      if (aiServiceIcon) {
        return aiServiceIcon;
      }
    }

    // Fallback: Try to derive from resource type
    const aiServiceIcon = getAgentIconUrl(node.data.resource.type);

    if (aiServiceIcon) {
      return aiServiceIcon;
    } else {
      // Bot but not in AiService enum - show generic bot icon
      return getResourceColoredIconPath(ResourceCategory.bot);
    }
  }

  // For tool nodes (category: tools), try to get tool-specific icon
  if (node.data.resource.category === ResourceCategory.tools) {
    const toolIcon = getAgentIconUrl(node.data.resource.type);

    if (toolIcon) {
      return toolIcon;
    }
  }

  // Fall back to category-based icon
  return getResourceColoredIconPath(node.data.resource.category);
};

export const addClipPath = (
  svg: Selection<SVGSVGElement, InventoryGraphNode, HTMLElement, InventoryGraphNode>,
  clipPathId: string
) =>
  svg
    .append('clipPath')
    .attr('id', clipPathId)
    .append('circle')
    .attr('r', radius)
    .attr('cx', radius)
    .attr('cy', radius);

export const fullScreenBoundingBox = () =>
  DOMRect.fromRect({
    x: fullScreenMargin,
    y: fullScreenMargin,
    width: window.innerWidth - 2 * fullScreenMargin,
    height: window.innerHeight - 2 * fullScreenMargin,
  });
